import { useState } from 'react';
import { Loader2, Copy, Check, Download, RefreshCw, ChevronDown, AlertCircle, BadgeCheck } from 'lucide-react';
import { copyToClipboard, xhrBlob } from './storyboardApi';

export default function SceneCard({ scene, onRegenerate }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const n = scene.scene_number;

  const copyAnim = async () => {
    await copyToClipboard(scene.animation_prompt || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const download = async () => {
    if (!scene.image_url) return;
    setDownloading(true);
    const blob = await xhrBlob(scene.image_url);
    setDownloading(false);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scene-${String(n).padStart(2, '0')}-${(scene.title || 'storyboard').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const inFlight = scene.status === 'pending' || scene.status === 'generating';

  return (
    <div
      className="rounded-2xl border border-gray-200/50 bg-white/40 overflow-hidden flex flex-col"
      data-testid={`sb-scene-card-${n}`}
    >
      {/* Image area */}
      <div className="relative aspect-video bg-gray-100/70 flex items-center justify-center">
        {scene.status === 'done' && scene.image_url ? (
          <img src={scene.image_url} alt={scene.title || `Scene ${n}`} className="w-full h-full object-cover" loading="lazy" />
        ) : scene.status === 'failed' ? (
          <div className="text-center px-4">
            <AlertCircle className="w-5 h-5 text-red-400 mx-auto mb-1.5" />
            <p className="text-[11px] text-red-500 line-clamp-2">{scene.error_message || 'Generation failed'}</p>
          </div>
        ) : (
          <div className="text-center">
            <Loader2 className="w-5 h-5 text-gray-300 animate-spin mx-auto mb-1.5" />
            <p className="text-[11px] text-gray-400">{scene.status === 'generating' ? 'Generating...' : 'Queued'}</p>
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-gray-900/80 text-white text-[10px] font-mono">
          {String(n).padStart(2, '0')}
        </span>
        {scene.uses_logo && (
          <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 text-gray-700 text-[10px] font-medium">
            <BadgeCheck className="w-3 h-3" /> Logo
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-3.5 space-y-2.5 flex-1 flex flex-col">
        <div>
          <p className="text-sm font-medium text-gray-900">{scene.title || `Scene ${n}`}</p>
          {scene.visual_description && (
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{scene.visual_description}</p>
          )}
        </div>

        {/* Image prompt (collapsible) */}
        {scene.image_prompt && (
          <div>
            <button
              data-testid={`sb-toggle-imgprompt-${n}`}
              onClick={() => setShowImagePrompt(v => !v)}
              className="flex items-center gap-1 text-[10px] text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors"
            >
              Image prompt <ChevronDown className={`w-3 h-3 transition-transform ${showImagePrompt ? 'rotate-180' : ''}`} />
            </button>
            {showImagePrompt && (
              <p className="text-[11px] text-gray-500 mt-1 leading-relaxed bg-white/50 border border-gray-200/40 rounded-lg p-2">
                {scene.image_prompt}
              </p>
            )}
          </div>
        )}

        {/* Animation prompt */}
        {scene.animation_prompt && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Animation prompt</p>
            <p className="text-[11px] text-gray-600 leading-relaxed bg-white/50 border border-gray-200/40 rounded-lg p-2">
              {scene.animation_prompt}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1.5 pt-1 mt-auto">
          <button
            data-testid={`sb-copy-anim-${n}`}
            onClick={copyAnim}
            disabled={!scene.animation_prompt}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-white/60 border border-gray-200/60 text-gray-600 hover:text-gray-900 hover:bg-white/80 transition-all disabled:opacity-40"
          >
            {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            <span>{copied ? 'Copied' : 'Copy motion'}</span>
          </button>
          <button
            data-testid={`sb-download-${n}`}
            onClick={download}
            disabled={scene.status !== 'done' || downloading}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-white/60 border border-gray-200/60 text-gray-600 hover:text-gray-900 hover:bg-white/80 transition-all disabled:opacity-40"
          >
            {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Download
          </button>
          <button
            data-testid={`sb-regen-${n}`}
            onClick={onRegenerate}
            disabled={inFlight}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-white/60 border border-gray-200/60 text-gray-600 hover:text-gray-900 hover:bg-white/80 transition-all disabled:opacity-40"
          >
            <RefreshCw className={`w-3 h-3 ${inFlight ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        </div>
      </div>
    </div>
  );
}
